import { BlobItem } from '@/types';
import { config, getContentType, sanitizeFileName } from '@/config';
import { authService } from './authService';
import { connectionState } from './connectionState';
import { siteContext } from './siteContext';
import { logger } from '@/utils/logger';
import { trackEvent } from '@/services/telemetry';

// Path-only (no query string / token) for safe telemetry properties.
function safeUrlPath(url: string): string {
  try {
    return new URL(url).pathname;
  } catch {
    return url.split('?')[0];
  }
}

function parseAuthChallenge(header: string | null): { error?: string; claims?: string } {
  if (!header) return {};
  const out: Record<string, string> = {};
  const re = /(\w+)="([^"]*)"/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(header)) !== null) {
    out[match[1]] = match[2];
  }
  return out;
}

function decodeClaims(value: string): string {
  try {
    return atob(value);
  } catch {
    return value;
  }
}

// The add-in's single storage backend: SharePoint document libraries via
// Microsoft Graph. Opening items happens through their SharePoint webUrl
// (store-level); this service only moves bytes and metadata.
export interface ISharePointService {
  // Multi-site: select which SharePoint site subsequent calls target.
  setSiteId(id: string | null): void;
  // Which of the given sites the signed-in user can access (batched probe).
  sitesAccessible(siteIds: string[]): Promise<Record<string, boolean | undefined>>;
  listItems(path: string): Promise<BlobItem[]>;
  // Recursive search of a folder subtree (empty path = whole site drive).
  searchItems(query: string, scopePath?: string): Promise<BlobItem[]>;
  createFolder(currentPath: string, folderName: string): Promise<void>;
  uploadFile(
    file: Blob,
    destinationPath: string,
    originalFileName: string,
    contentHash?: string,
    onProgress?: (progress: number) => void,
    signal?: AbortSignal,
    emailReceivedDate?: Date,
    hasAttachments?: boolean,
  ): Promise<string>;
  deleteBlob(path: string): Promise<void>;
  deleteFolder(folderPath: string): Promise<void>;
  // Move a file or folder (with children) into another folder on this drive.
  moveItem(srcPath: string, destFolderPath: string): Promise<void>;
  renameItem(path: string, newName: string): Promise<void>;
  downloadBlob(item: BlobItem): Promise<void>;
  getFileContent(path: string): Promise<string>;
  // Short-lived embeddable preview URL (Office/PDF, server-rendered).
  getPreviewUrl(path: string): Promise<string>;
  getFileBlob(path: string): Promise<Blob>;
  generateContentHash(content: string | ArrayBuffer): Promise<string>;
  getBlobUrl(path: string): string;
}

const GRAPH_BASE = 'https://graph.microsoft.com/v1.0';
// Graph uploadSession is required for files larger than 4 MB; smaller files
// use the simple PUT /content endpoint.
const SIMPLE_UPLOAD_LIMIT = 4 * 1024 * 1024;
// Upload session chunks must be a multiple of 320 KiB.
const UPLOAD_CHUNK = 32 * 320 * 1024; // 10 MB (must be a multiple of 320 KiB); larger chunks make multi-GB uploads viable

// Throttling/transient retry policy. SharePoint and Graph reject bulk writes
// with 429 (and transient 503/504); the request is not processed, so a retry is
// safe (no duplicate uploads).
const MAX_RETRIES = 5;
const MAX_RETRY_WAIT_MS = 60_000;

// Randomized backoff for the same-name upload rename race (409). The randomness
// is the point: concurrent uploads of identically-named files (e.g. 20 emails
// with the same subject) race on conflictBehavior=rename and collide on the
// same "(n)" suffix; a de-correlated wait lets the namespace settle so each
// retry picks a different name.
function conflictBackoffMs(attempt: number): number {
  return 150 + attempt * 200 + Math.floor(Math.random() * 400);
}

// Milliseconds to wait before a retry: honor the Retry-After header (seconds or
// HTTP date) when present, else capped exponential backoff with jitter.
function retryAfterMs(response: Response, attempt: number): number {
  const header = response.headers?.get?.('Retry-After');
  if (header) {
    const seconds = Number(header);
    if (Number.isFinite(seconds)) return Math.min(seconds * 1000, MAX_RETRY_WAIT_MS);
    const when = Date.parse(header);
    if (!Number.isNaN(when)) return Math.min(Math.max(when - Date.now(), 0), MAX_RETRY_WAIT_MS);
  }
  const backoff = Math.min(500 * 2 ** attempt, MAX_RETRY_WAIT_MS);
  return backoff + Math.floor(Math.random() * 250);
}

// A setTimeout that rejects with AbortError if the signal fires during the wait.
function delayWithAbort(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort);
  });
}

interface GraphDriveItem {
  id: string;
  name: string;
  size?: number;
  lastModifiedDateTime?: string;
  folder?: { childCount?: number };
  file?: { mimeType?: string; hashes?: { quickXorHash?: string; sha256Hash?: string } };
  '@microsoft.graph.downloadUrl'?: string;
  webUrl?: string;
  listItem?: { fields?: Record<string, unknown> };
  parentReference?: { path?: string; driveId?: string; siteId?: string };
  thumbnails?: Array<{
    small?: { url: string };
    medium?: { url: string };
    large?: { url: string };
  }>;
}

interface GraphListResponse {
  value: GraphDriveItem[];
  '@odata.nextLink'?: string;
}

const CUSTOM_LIST_FIELDS = ['EmailReceivedDate', 'HasAttachments'] as const;
const LIST_ITEM_EXPAND = `listItem($expand=fields($select=${CUSTOM_LIST_FIELDS.join(',')}))`;
let listingFieldFallbackLogged = false;
let searchFieldFallbackLogged = false;
let receivedDateWriteWarningLogged = false;
let malformedHasAttachmentsLogged = false;

function getField(fields: Record<string, unknown>, name: string): unknown {
  const key = Object.keys(fields).find(
    (candidate) => candidate.toLowerCase() === name.toLowerCase(),
  );
  return key ? fields[key] : undefined;
}

function parseHasAttachments(value: unknown): boolean | undefined {
  if (value === true || value === false) return value;
  if (value === 1 || value === '1' || value === 'true') return true;
  if (value === 0 || value === '0' || value === 'false') return false;
  if (value !== undefined && value !== null && !malformedHasAttachmentsLogged) {
    malformedHasAttachmentsLogged = true;
    logger.warn('Ignoring malformed HasAttachments field value.');
  }
  return undefined;
}

/**
 * SharePoint-backed storage service that talks to Microsoft Graph directly
 * using a delegated token acquired via Nested App Authentication (NAA).
 *
 * There is no backend (no BFF/OBO) and no Azure storage account; all calls
 * target the SharePoint document library exposed as the site's default drive.
 */
class SharePointService implements ISharePointService {
  private recoveryInFlight: Promise<string> | null = null;

  private get siteId(): string {
    // The selected site (multi-site) falls back to the configured default.
    return siteContext.getSiteId() || config.sharePoint.siteId;
  }

  setSiteId(id: string | null): void {
    siteContext.setSiteId(id);
  }

  // Which of the given sites the signed-in user can access (used by the home view
  // to show only reachable sites). Probed in a single Graph $batch so the per-site
  // 403s land inside the batch response body instead of as separate failed network
  // requests the browser would log to the console. A 2xx sub-status means access.
  // Returns per-site access: true (accessible), false (genuinely denied: 403/404),
  // or undefined (uncertain: a transient sub-status like 429/5xx). THROWS if the
  // probe itself fails (network error, batch non-2xx, token error) so the caller
  // can keep the last-known access instead of treating every site as denied
  // during a transient blip (e.g. a deploy cache-convergence window).
  async sitesAccessible(siteIds: string[]): Promise<Record<string, boolean | undefined>> {
    const result: Record<string, boolean | undefined> = {};
    if (siteIds.length === 0) return result;
    const headers = await this.authHeaders();
    headers['Content-Type'] = 'application/json';
    const requests = siteIds.map((id, i) => ({
      id: String(i),
      method: 'GET',
      url: `/sites/${id}/drive?$select=id`,
    }));
    const res = await this.fetchGraph(`${GRAPH_BASE}/$batch`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ requests }),
    });
    if (!res.ok) {
      throw Object.assign(new Error(`Site access probe failed: ${res.status}`), {
        status: res.status,
      });
    }
    const data = (await res.json()) as { responses?: Array<{ id: string; status: number }> };
    const statusById = new Map((data.responses || []).map((r) => [r.id, r.status]));
    siteIds.forEach((id, i) => {
      const status = statusById.get(String(i)) ?? 0;
      if (status >= 200 && status < 300) result[id] = true;
      else if (status === 401 || status === 429 || status >= 500)
        result[id] = undefined; // auth (stale token) or transient - NOT a denial
      else result[id] = false; // 403/404 etc. - genuinely no access
    });
    return result;
  }

  private get driveRoot(): string {
    if (!this.siteId) {
      throw new Error('SharePoint site id not configured (VITE_SP_SITE_ID).');
    }
    // The composite site id is "host,siteGuid,webGuid" - the commas must stay
    // literal (encodeURIComponent would turn them into %2C and Graph 400s).
    return `${GRAPH_BASE}/sites/${this.siteId}/drive`;
  }

  private async authHeaders(): Promise<Record<string, string>> {
    const token = await authService.getGraphToken();
    return { Authorization: `Bearer ${token}` };
  }

  // Build a Graph path addressing helper for a given store-relative path.
  // Empty path -> drive root; otherwise root:/{path}:
  private itemPath(path: string): string {
    const clean = this.normalizePath(path);
    if (!clean) {
      return `${this.driveRoot}/root`;
    }
    return `${this.driveRoot}/root:/${this.encodePath(clean)}:`;
  }

  private normalizePath(path: string): string {
    return (path || '').replace(/^\/+/, '').replace(/\/+$/, '');
  }

  private encodePath(path: string): string {
    return path
      .split('/')
      .filter(Boolean)
      .map((segment) => encodeURIComponent(segment))
      .join('/');
  }

  private toBlobItem(item: GraphDriveItem, parentPath: string): BlobItem {
    const isFolder = !!item.folder;
    const prefix = parentPath ? `${this.normalizePath(parentPath)}/` : '';
    const path = `${prefix}${item.name}`;
    const fields = item.listItem?.fields || {};
    const metadata: Record<string, string> = {};
    const contentHash = getField(fields, 'ContentHash');
    const originalName = getField(fields, 'OriginalName');
    if (typeof contentHash === 'string') metadata.contenthash = contentHash;
    if (typeof originalName === 'string') metadata.originalname = originalName;

    const blob: BlobItem = {
      name: item.name,
      path: isFolder ? `${path}/` : path,
      isFolder,
      metadata,
      hasAttachments: parseHasAttachments(getField(fields, 'HasAttachments')),
    };
    if (typeof item.size === 'number') blob.size = item.size;
    if (item.lastModifiedDateTime) blob.lastModified = new Date(item.lastModifiedDateTime);
    const receivedValue = getField(fields, 'EmailReceivedDate');
    if (typeof receivedValue === 'string') {
      const receivedDate = new Date(receivedValue);
      if (Number.isFinite(receivedDate.getTime())) blob.emailReceivedDate = receivedDate;
    }
    if (item.file?.mimeType) blob.contentType = item.file.mimeType;
    const thumb = item.thumbnails?.[0];
    if (thumb) blob.thumbnailUrl = thumb.medium?.url || thumb.small?.url;
    if (item.webUrl) blob.webUrl = item.webUrl;
    return blob;
  }

  // Fetch custom email/data fields in the same paged response as the rows. If a
  // library is not provisioned (or fields are unreadable), retry once without
  // custom fields and treat the values as unknown.
  async listItems(path: string = ''): Promise<BlobItem[]> {
    const headers = await this.authHeaders();
    const base = this.itemPath(path);
    const fallbackUrl = `${base}/children?$top=200&$expand=thumbnails`;
    let url = `${base}/children?$top=200&$expand=thumbnails,${LIST_ITEM_EXPAND}`;
    let readingCustomFields = true;
    const items: BlobItem[] = [];

    while (url) {
      const response = await this.fetchGraph(url, { headers });
      if (!response.ok) {
        if (response.status === 404) return [];
        if (readingCustomFields && (response.status === 400 || response.status === 403)) {
          if (!listingFieldFallbackLogged) {
            listingFieldFallbackLogged = true;
            logger.warn('SharePoint custom fields unavailable; listing without email metadata.');
          }
          readingCustomFields = false;
          items.length = 0;
          url = fallbackUrl;
          continue;
        }
        throw Object.assign(
          new Error(`Failed to list items: ${await this.errorDetail(response)}`),
          { status: response.status },
        );
      }
      const data = (await response.json()) as GraphListResponse;
      for (const entry of data.value) {
        items.push(this.toBlobItem(entry, path));
      }
      url = data['@odata.nextLink'] || '';
    }

    return items.sort((a, b) => {
      if (a.isFolder && !b.isFolder) return -1;
      if (!a.isFolder && b.isFolder) return 1;
      return a.name.localeCompare(b.name);
    });
  }

  // Recursive search across the current site's drive (every subfolder), replacing
  // the old folder-local substring filter. Result items carry their full path.
  async searchItems(query: string, scopePath = ''): Promise<BlobItem[]> {
    const trimmed = query.trim();
    if (!trimmed) return [];
    const headers = await this.authHeaders();
    // The drive search() function takes the term in single quotes; double any.
    const q = encodeURIComponent(trimmed.replace(/'/g, "''"));
    const searchBase = `${this.itemPath(scopePath)}/search(q='${q}')?$top=200`;
    const fallbackUrl = `${searchBase}&$expand=thumbnails`;
    let url = `${searchBase}&$expand=thumbnails,${LIST_ITEM_EXPAND}`;
    let readingCustomFields = true;
    const items: BlobItem[] = [];

    while (url) {
      const response = await this.fetchGraph(url, { headers });
      if (!response.ok) {
        if (response.status === 404) return [];
        if (readingCustomFields && (response.status === 400 || response.status === 403)) {
          if (!searchFieldFallbackLogged) {
            searchFieldFallbackLogged = true;
            logger.warn('SharePoint custom fields unavailable; searching without email metadata.');
          }
          readingCustomFields = false;
          items.length = 0;
          url = fallbackUrl;
          continue;
        }
        throw new Error(`Search failed: ${await this.errorDetail(response)}`);
      }
      const data = (await response.json()) as GraphListResponse;
      for (const entry of data.value) {
        items.push(this.toSearchBlobItem(entry));
      }
      url = data['@odata.nextLink'] || '';
    }

    return items.sort((a, b) => {
      if (a.isFolder && !b.isFolder) return -1;
      if (!a.isFolder && b.isFolder) return 1;
      return a.name.localeCompare(b.name);
    });
  }

  // A search result lives anywhere in the drive, so derive its parent path from
  // the item's parentReference (".../root:/Folder/Sub") rather than a fixed path.
  private toSearchBlobItem(item: GraphDriveItem): BlobItem {
    const refPath = item.parentReference?.path || '';
    const afterRoot = refPath.includes('root:') ? refPath.split('root:')[1] : '';
    const parent = decodeURIComponent(afterRoot).replace(/^\/+/, '').replace(/\/+$/, '');
    return this.toBlobItem(item, parent);
  }

  async createFolder(currentPath: string, folderName: string): Promise<void> {
    const headers = await this.authHeaders();
    headers['Content-Type'] = 'application/json';
    const url = `${this.itemPath(currentPath)}/children`;

    const response = await this.fetchGraph(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        name: folderName,
        folder: {},
        '@microsoft.graph.conflictBehavior': 'fail',
      }),
    });

    // 409 = folder already exists; treat as success.
    if (!response.ok && response.status !== 409) {
      throw new Error(`Failed to create folder: ${await this.errorDetail(response)}`);
    }
  }

  async uploadFile(
    file: Blob,
    destinationPath: string,
    originalFileName: string,
    contentHash?: string,
    onProgress?: (progress: number) => void,
    signal?: AbortSignal,
    emailReceivedDate?: Date,
    hasAttachments?: boolean,
  ): Promise<string> {
    if (file.size > config.features.maxUploadSize) {
      throw new Error(
        `File exceeds maximum upload size of ${config.features.maxUploadSize} bytes.`,
      );
    }

    const destDir = this.normalizePath(destinationPath);
    // Strip characters SharePoint rejects (the colon in "RE: ..." emails, etc.),
    // which also break Graph's root:/path: addressing.
    const safeName = sanitizeFileName(originalFileName) || 'untitled';
    const itemPath = destDir ? `${destDir}/${safeName}` : safeName;

    let itemId: string | undefined;
    if (file.size <= SIMPLE_UPLOAD_LIMIT) {
      itemId = await this.simpleUpload(itemPath, file, safeName, signal);
      onProgress?.(100);
    } else {
      itemId = await this.sessionUpload(itemPath, file, onProgress, signal);
    }

    // Stamp fields separately so an unprovisioned additive date field cannot
    // prevent the established ContentHash field from being saved.
    if (itemId && contentHash) {
      await this.setItemFields(itemId, { ContentHash: contentHash }).catch((e) =>
        logger.warn('Metadata write skipped:', e instanceof Error ? e.message : e),
      );
    }
    if (itemId && emailReceivedDate && Number.isFinite(emailReceivedDate.getTime())) {
      await this.setItemFields(itemId, {
        EmailReceivedDate: emailReceivedDate.toISOString(),
      }).catch((e) => {
        if (!receivedDateWriteWarningLogged) {
          receivedDateWriteWarningLogged = true;
          logger.warn('EmailReceivedDate write skipped:', e instanceof Error ? e.message : e);
        }
      });
    }
    if (itemId && hasAttachments !== undefined) {
      await this.setItemFields(itemId, { HasAttachments: hasAttachments }).catch((e) =>
        logger.warn('Attachment metadata write skipped:', e instanceof Error ? e.message : e),
      );
    }

    return safeName;
  }

  // fetch that converts opaque network-level failures (offline, DNS, CORS,
  // host blocking) into an actionable message instead of "Failed to fetch".
  private recoverToken(hasClaims: boolean, claims?: string): Promise<string> {
    if (!this.recoveryInFlight) {
      const acquire =
        hasClaims && claims
          ? authService.acquireGraphWithClaims(decodeClaims(claims))
          : authService.getGraphToken(undefined, true);
      this.recoveryInFlight = acquire.finally(() => {
        this.recoveryInFlight = null;
      });
    }
    return this.recoveryInFlight;
  }

  private async fetchGraph(
    url: string,
    init?: RequestInit,
    attempt = 0,
    authStage = 0,
    retryLocks = true,
  ): Promise<Response> {
    let response: Response;
    try {
      response = await fetch(url, init);
    } catch (error) {
      if ((error as { name?: string })?.name === 'AbortError') {
        throw error;
      }
      logger.warn('Graph request failed at the network layer:', error);
      throw new Error(
        'Network error reaching Microsoft Graph. Check your connection and sign-in, then try again.',
      );
    }

    if (response.status === 401 && authStage < 1) {
      const nextStage = authStage + 1;
      const challenge = parseAuthChallenge(response.headers.get('www-authenticate'));
      const hasClaims = challenge.error === 'insufficient_claims' && !!challenge.claims;
      logger.warn(`Graph 401; token recovery (claims challenge: ${hasClaims})`);
      trackEvent('auth.token401Recovery', {
        path: safeUrlPath(url),
        stage: nextStage,
        claimsChallenge: hasClaims,
      });
      try {
        const token = await this.recoverToken(hasClaims, challenge.claims);
        const freshHeaders: Record<string, string> = {
          ...(init?.headers as Record<string, string> | undefined),
          Authorization: `Bearer ${token}`,
        };
        return this.fetchGraph(
          url,
          { ...init, headers: freshHeaders },
          attempt,
          nextStage,
          retryLocks,
        );
      } catch (error) {
        logger.error('Token recovery failed; surfacing the 401:', error);
        connectionState.setStale(true);
        return response;
      }
    }

    // Retry throttling (429), transient server errors (503/504), and short-term
    // locks (423): SharePoint briefly locks an item after it is opened/previewed
    // or just written, and Graph (unlike the SharePoint UI) rejects a
    // rename/move/delete with 423 instead of waiting. The lock usually clears in
    // a few seconds, so back off and retry. Honors Retry-After when present.
    if (
      (response.status === 429 ||
        (response.status === 423 && retryLocks) ||
        response.status === 503 ||
        response.status === 504) &&
      attempt < MAX_RETRIES
    ) {
      const waitMs = retryAfterMs(response, attempt);
      logger.warn(
        `Graph ${response.status} (retryable); retrying in ${waitMs}ms (attempt ${attempt + 1} of ${MAX_RETRIES})`,
      );
      await delayWithAbort(waitMs, init?.signal ?? undefined);
      return this.fetchGraph(url, init, attempt + 1, authStage, retryLocks);
    }

    if (response.ok) {
      connectionState.setStale(false);
    } else if (response.status === 401) {
      connectionState.setStale(true);
    }
    return response;
  }

  // Pull a human-readable reason out of a failed Graph response (the JSON
  // error.message - e.g. a blocked file type or quota - falling back to status).
  private async errorDetail(response: Response): Promise<string> {
    let detail = response.statusText;
    try {
      const body = await response.clone().json();
      detail = body?.error?.message || detail;
    } catch {
      // non-JSON / empty body
    }
    return `${response.status} ${detail}`;
  }

  private async simpleUpload(
    itemPath: string,
    file: Blob,
    originalFileName: string,
    signal?: AbortSignal,
    attempt = 0,
  ): Promise<string | undefined> {
    const headers = await this.authHeaders();
    headers['Content-Type'] = getContentType(originalFileName);
    const url = `${this.driveRoot}/root:/${this.encodePath(itemPath)}:/content?@microsoft.graph.conflictBehavior=rename`;

    const response = await this.fetchGraph(url, { method: 'PUT', headers, body: file, signal });
    // Same-name concurrent uploads race on conflictBehavior=rename and one gets
    // 409 nameAlreadyExists. The file was not written, so retry after a
    // randomized delay and the rename will settle on a free name.
    if (response.status === 409 && attempt < MAX_RETRIES) {
      await delayWithAbort(conflictBackoffMs(attempt), signal);
      return this.simpleUpload(itemPath, file, originalFileName, signal, attempt + 1);
    }
    if (!response.ok) {
      throw new Error(`Upload failed: ${await this.errorDetail(response)}`);
    }
    const item = (await response.json().catch(() => ({}))) as { id?: string };
    return item.id;
  }

  private async sessionUpload(
    itemPath: string,
    file: Blob,
    onProgress?: (progress: number) => void,
    signal?: AbortSignal,
  ): Promise<string | undefined> {
    const headers = await this.authHeaders();
    headers['Content-Type'] = 'application/json';
    const createUrl = `${this.driveRoot}/root:/${this.encodePath(itemPath)}:/createUploadSession`;

    const createResponse = await this.fetchGraph(createUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        item: { '@microsoft.graph.conflictBehavior': 'rename' },
      }),
    });
    if (!createResponse.ok) {
      throw new Error(`Failed to create upload session: ${await this.errorDetail(createResponse)}`);
    }
    const session = (await createResponse.json()) as { uploadUrl: string };

    let offset = 0;
    const total = file.size;
    while (offset < total) {
      const end = Math.min(offset + UPLOAD_CHUNK, total);
      const chunk = file.slice(offset, end);
      // Upload session chunk requests are sent to the pre-authenticated
      // uploadUrl and must NOT carry the Authorization header.
      const chunkResponse = await this.fetchGraph(session.uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Length': `${end - offset}`,
          'Content-Range': `bytes ${offset}-${end - 1}/${total}`,
        },
        body: chunk,
        signal,
      });
      if (!chunkResponse.ok && chunkResponse.status !== 201 && chunkResponse.status !== 202) {
        throw new Error(`Chunk upload failed: ${await this.errorDetail(chunkResponse)}`);
      }
      offset = end;
      onProgress?.((offset / total) * 100);

      // The final chunk (201/202) returns the completed driveItem.
      if (offset >= total) {
        const item = (await chunkResponse.json().catch(() => ({}))) as { id?: string };
        return item.id;
      }
    }
    return undefined;
  }

  // Stamp data-platform metadata columns on a drive item, addressed by id so a
  // conflictBehavior=rename during upload cannot point us at the wrong path.
  private async setItemFields(itemId: string, fields: Record<string, unknown>): Promise<void> {
    const headers = await this.authHeaders();
    headers['Content-Type'] = 'application/json';
    const url = `${this.driveRoot}/items/${itemId}/listItem/fields`;
    const response = await this.fetchGraph(url, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(fields),
    });
    if (!response.ok) {
      throw new Error(`Failed to set item fields: ${await this.errorDetail(response)}`);
    }
  }

  async deleteBlob(path: string): Promise<void> {
    const headers = await this.authHeaders();
    const response = await this.fetchGraph(this.itemPath(path), { method: 'DELETE', headers });
    if (!response.ok && response.status !== 404) {
      throw new Error(`Failed to delete: ${await this.errorDetail(response)}`);
    }
  }

  async deleteFolder(folderPath: string): Promise<void> {
    // Graph DELETE on a folder removes it recursively.
    await this.deleteBlob(folderPath);
  }

  async moveItem(srcPath: string, destFolderPath: string): Promise<void> {
    const headers = await this.authHeaders();
    headers['Content-Type'] = 'application/json';
    const destClean = this.normalizePath(destFolderPath);
    // Same-drive move: PATCH the item with its new parent. "/drive/root:"
    // addresses the drive root; folders move with their children.
    const parentPath = destClean ? `/drive/root:/${destClean}` : '/drive/root:';
    const response = await this.fetchGraph(this.itemPath(srcPath), {
      method: 'PATCH',
      headers,
      body: JSON.stringify({
        parentReference: { path: parentPath },
        '@microsoft.graph.conflictBehavior': 'rename',
      }),
    });
    if (!response.ok) {
      const detail = await this.errorDetail(response);
      if (
        response.status === 404 ||
        (response.status === 400 && detail.includes("Either 'folder' or 'file'"))
      ) {
        throw new Error(
          'Could not move the item - it may have already been moved. Refresh and check the destination.',
        );
      }
      throw new Error(`Failed to move: ${detail}`);
    }
  }

  // Rename a file or folder in place: PATCH the drive item's name. Because the
  // add-in and SharePoint are the same item, this updates everywhere. A name
  // collision returns 409 nameAlreadyExists, surfaced as a clear message.
  async renameItem(path: string, newName: string): Promise<void> {
    const headers = await this.authHeaders();
    headers['Content-Type'] = 'application/json';
    // Try Graph first. Do NOT retry a 423 here (retryLocks=false): a file that is
    // open in SharePoint stays locked for the whole session, so instead of
    // waiting it out we fall straight to the SharePoint REST list-item rename,
    // which tolerates the lock exactly like the SharePoint UI does.
    const response = await this.fetchGraph(
      this.itemPath(path),
      { method: 'PATCH', headers, body: JSON.stringify({ name: newName }) },
      0,
      0,
      false,
    );
    if (response.status === 409) {
      throw new Error('A file or folder with that name already exists.');
    }
    if (response.status === 423) {
      logger.info('Rename hit 423 (locked); falling back to SharePoint REST list-item rename');
      trackEvent('rename.lockedFallback', { path: safeUrlPath(this.itemPath(path)) });
      return this.renameViaListItem(path, newName);
    }
    if (!response.ok) {
      throw new Error(`Failed to rename: ${await this.errorDetail(response)}`);
    }
  }

  // Lock-tolerant rename via the SharePoint REST list-item API (what the
  // SharePoint UI uses), for files Graph refuses to rename with 423 because they
  // are open. Resolves the file's SharePoint list/item/site ids from Graph, then
  // sets FileLeafRef via validateUpdateListItem using a SharePoint-audience token.
  async renameViaListItem(path: string, newName: string): Promise<void> {
    const idResp = await this.fetchGraph(`${this.itemPath(path)}?$select=sharepointIds`);
    if (!idResp.ok) {
      throw new Error(
        'This file is locked and could not be renamed. Close it in SharePoint and try again.',
      );
    }
    const sp = ((await idResp.json()) as { sharepointIds?: Record<string, string> }).sharepointIds;
    if (!sp?.listId || !sp?.listItemId || !sp?.siteUrl) {
      throw new Error('Could not resolve the SharePoint item to rename a locked file.');
    }

    const token = await authService.getSharePointToken();
    const base = sp.siteUrl.replace(/\/+$/, '');

    // SharePoint REST writes need a form digest.
    const ctxRes = await fetch(`${base}/_api/contextinfo`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, Accept: 'application/json;odata=nometadata' },
    });
    if (!ctxRes.ok) {
      throw new Error(`Failed to rename (locked): SharePoint context ${ctxRes.status}`);
    }
    const ctx = (await ctxRes.json()) as {
      FormDigestValue?: string;
      d?: { GetContextWebInformation?: { FormDigestValue?: string } };
    };
    const digest = ctx.FormDigestValue ?? ctx.d?.GetContextWebInformation?.FormDigestValue ?? '';

    // validateUpdateListItem sets the filename (FileLeafRef) and tolerates the
    // open-file lock, unlike Graph's driveItem rename.
    const url = `${base}/_api/web/lists(guid'${sp.listId}')/items(${sp.listItemId})/validateUpdateListItem`;
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'X-RequestDigest': digest,
        Accept: 'application/json;odata=nometadata',
        'Content-Type': 'application/json;odata=nometadata',
      },
      body: JSON.stringify({ formValues: [{ FieldName: 'FileLeafRef', FieldValue: newName }] }),
    });
    if (!res.ok) {
      throw new Error(`Failed to rename (locked): SharePoint ${res.status}`);
    }
    const data = (await res.json()) as {
      value?: Array<{ FieldName?: string; HasException?: boolean; ErrorMessage?: string }>;
      d?: {
        results?: Array<{ FieldName?: string; HasException?: boolean; ErrorMessage?: string }>;
      };
    };
    const results = data.value ?? data.d?.results ?? [];
    const failed = results.find((r) => r.HasException);
    if (failed) {
      throw new Error(
        `Failed to rename: ${failed.ErrorMessage || 'SharePoint rejected the change.'}`,
      );
    }
    trackEvent('rename.lockedFallback.ok');
  }

  async downloadBlob(item: BlobItem): Promise<void> {
    const blob = await this.getFileBlob(item.path);
    const downloadUrl = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = item.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(downloadUrl);
  }

  async getFileContent(path: string): Promise<string> {
    const blob = await this.getFileBlob(path);
    return blob.text();
  }

  // Short-lived embeddable preview URL (Office docs + PDF), rendered
  // server-side by SharePoint - no client download or parsing.
  async getPreviewUrl(path: string): Promise<string> {
    const headers = await this.authHeaders();
    headers['Content-Type'] = 'application/json';
    const response = await this.fetchGraph(`${this.itemPath(path)}/preview`, {
      method: 'POST',
      headers,
      body: '{}',
    });
    if (!response.ok) {
      throw new Error(`Failed to get preview: ${await this.errorDetail(response)}`);
    }
    const data = (await response.json()) as { getUrl?: string };
    if (!data.getUrl) {
      throw new Error('No embeddable preview for this item');
    }
    return data.getUrl;
  }

  async getFileBlob(path: string): Promise<Blob> {
    const headers = await this.authHeaders();
    const url = `${this.itemPath(path)}/content`;
    const response = await this.fetchGraph(url, { headers });
    if (!response.ok) {
      throw new Error(`Failed to get file: ${await this.errorDetail(response)}`);
    }
    return response.blob();
  }

  // SHA-256 hash of content, computed client-side via the Web Crypto API.
  async generateContentHash(content: string | ArrayBuffer): Promise<string> {
    const data =
      typeof content === 'string' ? new TextEncoder().encode(content) : new Uint8Array(content);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  }

  // Returns a Graph endpoint URL that streams the file content. The caller
  // must attach the NAA Graph token; this is not a public/pre-signed URL.
  getBlobUrl(path: string): string {
    return `${this.itemPath(path)}/content`;
  }
}

// Single shared instance used across the app.
export const sharePointService: ISharePointService = new SharePointService();
